import React, { useState, useEffect, useMemo, useRef } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useTheme } from '../contexts/ThemeContext';
import { apiService } from '../services/api';
import { renderAsync } from 'docx-preview';
import { replaceVariablesInHTML } from '../utils/docxParser';
import ReactQuill from 'react-quill';
import 'react-quill/dist/quill.snow.css';
import { Document, Packer, Paragraph, TextRun } from 'docx';
import jsPDF from 'jspdf';
import html2canvas from 'html2canvas';
import { API_BASE_URL } from '../config';

// Helper function to handle document download/view
const handleDocumentAction = async (filePath: string, fileName: string, isPdf: boolean) => {
  const token = localStorage.getItem('access_token');
  
  if (!token) {
    alert('Anda harus login untuk mengakses dokumen');
    return;
  }
  
  // Extract just the filename from path (remove 'uploads/documents/' prefix if exists)
  let cleanPath = filePath;
  if (cleanPath.startsWith('uploads/documents/')) {
    cleanPath = cleanPath.replace('uploads/documents/', '');
  }
  
  const url = `${API_BASE_URL}/api/documents/download/${cleanPath}`;
  
  try {
    const response = await fetch(url, {
      headers: {
        'Authorization': `Bearer ${token}`
      }
    });
    
    if (!response.ok) {
      if (response.status === 401) {
        alert('Sesi Anda telah berakhir. Silakan login kembali.');
        return;
      }
      throw new Error(`Failed to fetch document: ${response.status} ${response.statusText}`);
    }
    
    const blob = await response.blob();
    const blobUrl = window.URL.createObjectURL(blob);
    
    if (isPdf) {
      // Open PDF in new tab using a temporary link element
      // This approach is more reliable and won't affect the current page
      const link = document.createElement('a');
      link.href = blobUrl;
      link.target = '_blank';
      link.rel = 'noopener noreferrer';
      link.style.display = 'none';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      // Revoke URL after a delay to allow the new window to load
      // Keep the blob URL alive longer for PDF viewing
      setTimeout(() => {
        window.URL.revokeObjectURL(blobUrl);
      }, 2000);
    } else {
      // Download other files
      const link = document.createElement('a');
      link.href = blobUrl;
      link.download = fileName;
      link.style.display = 'none';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      // Revoke URL after a short delay
      setTimeout(() => {
        window.URL.revokeObjectURL(blobUrl);
      }, 100);
    }
  } catch (error) {
    console.error('Error handling document:', error);
    alert('Gagal membuka/mengunduh dokumen. Silakan coba lagi.');
  }
};

const TaskDrafting: React.FC = () => {
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();
  const { user } = useAuth();
  const { darkMode } = useTheme();
  
  const [task, setTask] = useState<any>(null);
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    categoryId: '',
    agreementTypeId: '',
    companyId: '',
    agreementNo: ''
  });
  
  const [categories, setCategories] = useState<any[]>([]);
  const [agreementTypes, setAgreementTypes] = useState<any[]>([]);
  const [companies, setCompanies] = useState<any[]>([]);
  const [template, setTemplate] = useState<any>(null);
  const [variables, setVariables] = useState<any[]>([]);
  const [userGroups, setUserGroups] = useState<number[]>([]);
  const [supportingDocuments, setSupportingDocuments] = useState<any[]>([]);
  const [users, setUsers] = useState<any[]>([]);
  const [assignments, setAssignments] = useState<any[]>([]);
  const [assignmentForm, setAssignmentForm] = useState({
    user_id: '',
    comment: ''
  });
  const [loadingAssignments, setLoadingAssignments] = useState(false);
  
  // Document preview states
  const previewContainerRef = useRef<HTMLDivElement>(null);
  const hiddenRenderContainerRef = useRef<HTMLDivElement>(null);
  const [fileArrayBuffer, setFileArrayBuffer] = useState<ArrayBuffer | null>(null);
  const [documentHtml, setDocumentHtml] = useState<string>('');
  const [editorContent, setEditorContent] = useState<string>('');
  const [leftPanelWidth, setLeftPanelWidth] = useState(400); // Default width 400px
  const [isResizing, setIsResizing] = useState(false);
  
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string>('');
  const [variablesExpanded, setVariablesExpanded] = useState(true);

  // Load task data and initialize form
  useEffect(() => {
    const loadTaskData = async () => {
      if (!id) {
        setError('Task ID tidak ditemukan');
        setLoading(false);
        return;
      }

      setLoading(true);
      try {
        // Load task data
        const taskData = await apiService.getTask(Number(id));
        setTask(taskData);
        
        // Load supporting documents from task
        if (taskData.supporting_documents && Array.isArray(taskData.supporting_documents)) {
          setSupportingDocuments(taskData.supporting_documents);
        }
        
        // Load categories, agreement types, companies, users, dan user groups secara parallel
        const [cats, types, comps, usersData, userGroupsData] = await Promise.all([
          apiService.getCategories(),
          apiService.getAgreementTypes(),
          apiService.getCompanies(),
          apiService.getUsers(),
          user?.id ? apiService.getUserGroups(user.id) : Promise.resolve([])
        ]);
        
        // Set state untuk categories dan agreementTypes
        setCategories(cats);
        setAgreementTypes(types);
        setCompanies(comps.filter((c: any) => c.is_active !== false));
        setUsers(usersData.filter((u: any) => u.is_active !== false));

        // Set user groups
        if (userGroupsData && userGroupsData.length > 0) {
          const groupIds = userGroupsData.map((ug: any) => ug.group_id);
          setUserGroups(groupIds);
        }
        
        // Set form data dari task
        setFormData({
          title: taskData.title || '',
          description: taskData.description || '',
          categoryId: taskData.category_id ? String(taskData.category_id) : '',
          agreementTypeId: taskData.agreement_type_id ? String(taskData.agreement_type_id) : '',
          companyId: taskData.company_id ? String(taskData.company_id) : '',
          agreementNo: taskData.agreement_no || ''
        });

        // Load template jika ada
        if (taskData.template_id) {
          try {
            const fullTemplate = await apiService.getTemplate(taskData.template_id);
            setTemplate(fullTemplate);
            
            // Initialize variables dari task (gunakan value yang sudah ada jika ada)
            if (fullTemplate && fullTemplate.variables) {
              const taskVariables = taskData.variables || [];
              const initializedVariables = fullTemplate.variables.map((v: any) => {
                // Cari variable yang sudah ada di task
                const existingVar = taskVariables.find((tv: any) => tv.name === v.name);
                return {
                  ...v,
                  value: existingVar?.value || ''
                };
              });
              setVariables(initializedVariables);
            }
            
            // Load template file untuk preview
            await loadTemplateFile(fullTemplate.id);
          } catch (err: any) {
            console.error('Error loading template:', err);
            setError('Gagal memuat template');
          }
        } else if (taskData.category_id && taskData.agreement_type_id) {
          // Try to load template from category and agreement type
          try {
            const templateData = await apiService.checkDuplicateTemplate(
              taskData.category_id,
              taskData.agreement_type_id
            );
            
            if (templateData) {
              const fullTemplate = await apiService.getTemplate(templateData.id);
              setTemplate(fullTemplate);
              
              if (fullTemplate && fullTemplate.variables) {
                const taskVariables = taskData.variables || [];
                const initializedVariables = fullTemplate.variables.map((v: any) => {
                  const existingVar = taskVariables.find((tv: any) => tv.name === v.name);
                  return {
                    ...v,
                    value: existingVar?.value || ''
                  };
                });
                setVariables(initializedVariables);
              }
              
              await loadTemplateFile(fullTemplate.id);
            }
          } catch (err: any) {
            console.error('Error loading template:', err);
          }
        }
      } catch (err: any) {
        console.error('Error loading task data:', err);
        setError('Gagal memuat data task. Silakan refresh halaman.');
      } finally {
        setLoading(false);
      }
    };

    loadTaskData();
  }, [id, user?.id]);

  // Load assignments when task is loaded
  useEffect(() => {
    const loadAssignments = async () => {
      if (!id) return;
      
      setLoadingAssignments(true);
      try {
        const assignmentsData = await apiService.getTaskAssignments(Number(id));
        setAssignments(assignmentsData);
      } catch (err: any) {
        console.error('Error loading assignments:', err);
      } finally {
        setLoadingAssignments(false);
      }
    };

    if (task) {
      loadAssignments();
    }
  }, [id, task]);

  // Update formData categoryId dan agreementTypeId setelah categories dan agreementTypes ter-load
  useEffect(() => {
    if (categories.length > 0 && agreementTypes.length > 0 && task) {
      const categoryId = task.category_id ? String(task.category_id) : '';
      const agreementTypeId = task.agreement_type_id ? String(task.agreement_type_id) : '';
      
      setFormData(prev => {
        if (prev.categoryId === categoryId && prev.agreementTypeId === agreementTypeId) {
          return prev; // No change needed
        }
        return {
          ...prev,
          categoryId: categoryId,
          agreementTypeId: agreementTypeId
        };
      });
    }
  }, [categories.length, agreementTypes.length, task]);

  // Load template file dari backend
  const loadTemplateFile = async (templateId: number) => {
    try {
      const token = localStorage.getItem('access_token');
      
      const response = await fetch(`${API_BASE_URL}/api/templates/${templateId}/file`, {
        headers: {
          ...(token && { 'Authorization': `Bearer ${token}` })
        }
      });
      
      if (!response.ok) {
        throw new Error('Gagal memuat file template');
      }
      
      const arrayBuffer = await response.arrayBuffer();
      setFileArrayBuffer(arrayBuffer);
    } catch (err: any) {
      console.error('Error loading template file:', err);
      setError('Gagal memuat file template');
    }
  };

  // Initial load document HTML (hanya sekali saat fileArrayBuffer pertama kali di-load)
  useEffect(() => {
    if (!fileArrayBuffer || documentHtml) {
      return; // Skip if already loaded or no file
    }

    let isMounted = true;

    const loadDocument = async () => {
      let retryCount = 0;
      const maxRetries = 20;

      const checkAndLoad = async () => {
        if (!isMounted) {
          return;
        }

        // Use hidden container for renderAsync to avoid React conflicts
        const renderContainer = hiddenRenderContainerRef.current;
        
        if (!renderContainer) {
          retryCount++;
          if (retryCount < maxRetries) {
            setTimeout(checkAndLoad, 100);
            return;
          } else {
            console.error('Container tidak ditemukan setelah beberapa retry');
            return;
          }
        }

        try {
          // Clear previous content safely
          if (renderContainer) {
            renderContainer.innerHTML = '';
          }

          // Render document to hidden container
          await renderAsync(
            fileArrayBuffer,
            renderContainer,
            undefined,
            {
              className: 'docx-wrapper',
              inWrapper: true,
              ignoreWidth: false,
              ignoreHeight: false,
              breakPages: true,
              ignoreLastRenderedPageBreak: true,
              experimental: false,
              trimXmlDeclaration: true,
              debug: false
            }
          );

          // Wait a bit for renderAsync to finish all DOM manipulations
          await new Promise(resolve => setTimeout(resolve, 100));

              // Get initial HTML (without variable replacement yet)
              if (renderContainer && isMounted) {
            try {
              const htmlContent = renderContainer.innerHTML;
              setDocumentHtml(htmlContent);
              // Don't set editorContent here, let auto-render handle it with variables
            } catch (err) {
              console.warn('Error setting initial document HTML:', err);
            }
          }
        } catch (err) {
          console.error('Error loading document:', err);
          setError('Gagal memuat dokumen');
        }
      };

      setTimeout(checkAndLoad, 200);
    };

    loadDocument();

    return () => {
      isMounted = false;
    };
  }, [fileArrayBuffer]); // Only run when fileArrayBuffer changes

  // Auto-render document dengan variables yang sudah ada saat documentHtml dan variables tersedia
  useEffect(() => {
    if (!documentHtml || variables.length === 0) {
      return;
    }

    // Check if there are any variables with values
    const hasVariableValues = variables.some((v: any) => v.value && v.value.trim() !== '');
    
    if (!hasVariableValues) {
      // No variables filled yet, show original document
      setEditorContent(documentHtml);
      return;
    }

    // Auto-render dengan variables yang sudah ada
    try {
      // Create variables map untuk replacement
      const variablesMap: Record<string, string> = {};
      variables.forEach((v: any) => {
        if (v.value) {
          variablesMap[v.name] = v.value;
        }
      });

      // Replace variables dalam HTML string
      const replacedHtml = replaceVariablesInHTML(documentHtml, variablesMap);
      
      // Update state - React will handle the re-render via dangerouslySetInnerHTML
      setEditorContent(replacedHtml);
    } catch (err) {
      console.error('Error auto-rendering document:', err);
      // Fallback to original document
      setEditorContent(documentHtml);
    }
  }, [documentHtml, variables]); // Auto-render when documentHtml or variables change

  // Function untuk render document dengan variable replacement (manual trigger)
  const handleRenderDocument = async () => {
    if (!documentHtml || !fileArrayBuffer) {
      setError('Dokumen belum dimuat. Silakan tunggu sebentar.');
      return;
    }

    try {
      // Create variables map untuk replacement
      const variablesMap: Record<string, string> = {};
      variables.forEach((v: any) => {
        if (v.value) {
          variablesMap[v.name] = v.value;
        }
      });

      // Replace variables dalam HTML string
      const replacedHtml = replaceVariablesInHTML(documentHtml, variablesMap);
      
      // Update state - React will handle the re-render via dangerouslySetInnerHTML
      setEditorContent(replacedHtml);
    } catch (err) {
      console.error('Error rendering document:', err);
      setError('Gagal merender dokumen');
    }
  };

  // Handle form submit (update task instead of create)
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!id) {
      setError('Task ID tidak ditemukan');
      return;
    }
    
    if (!formData.title || !formData.categoryId || !formData.agreementTypeId || !formData.companyId) {
      setError('Mohon lengkapi semua field yang wajib diisi');
      return;
    }

    // Check if all required variables are filled
    const requiredVariables = variables.filter((v: any) => isVariableForCurrentUser(v));
    const unfilledRequired = requiredVariables.filter((v: any) => !v.value || v.value.trim() === '');
    
    if (unfilledRequired.length > 0) {
      setError(`Mohon isi semua variable yang wajib: ${unfilledRequired.map((v: any) => v.name).join(', ')}`);
      return;
    }

    setSaving(true);
    setError('');

    try {
      // Prepare task update data
      const taskUpdateData = {
        title: formData.title,
        description: formData.description || null,
        agreement_no: formData.agreementNo || null,
        category_id: Number(formData.categoryId),
        agreement_type_id: Number(formData.agreementTypeId),
        company_id: formData.companyId ? Number(formData.companyId) : null,
        template_id: template?.id || null,
        status_id: 2, // Set status to "Drafting" (2)
        variables: variables.map((v: any) => ({
          name: v.name,
          position: v.position || 0,
          value: v.value || '',
          group_id: v.group_id || null
        }))
      };

      // Update task
      const updatedTask = await apiService.updateTask(Number(id), taskUpdateData);
      
      console.log('Task updated successfully:', updatedTask);
      
      // Show success message and redirect
      alert('Draft berhasil disimpan! Status task diubah menjadi Drafting.');
      navigate(`/agreement/${id}`);
    } catch (err: any) {
      console.error('Error updating task:', err);
      setError(err.message || 'Gagal menyimpan draft. Silakan coba lagi.');
    } finally {
      setSaving(false);
    }
  };

  // Handle assignment form submit
  const handleAssignmentSubmit = async (e?: React.MouseEvent) => {
    if (e) {
      e.preventDefault();
      e.stopPropagation(); // Prevent event bubbling
    }
    
    if (!id) {
      setError('Task ID tidak ditemukan');
      return;
    }

    if (!assignmentForm.user_id) {
      // Assignment is optional, so just return if no user selected
      return;
    }

    const assignmentData = {
      task_id: Number(id),
      user_id: Number(assignmentForm.user_id),
      comment: assignmentForm.comment || undefined
    };

    console.log('Creating assignment with data:', assignmentData);

    try {
      const result = await apiService.createTaskAssignment(assignmentData);
      console.log('Assignment created successfully:', result);

      // Reload assignments immediately (no delay needed)
      const assignmentsData = await apiService.getTaskAssignments(Number(id));
      console.log('Reloaded assignments:', assignmentsData);
      setAssignments(assignmentsData);

      // Reset form
      setAssignmentForm({
        user_id: '',
        comment: ''
      });

      // Clear error if any
      setError('');
      
      // Success - no alert, just update the UI
    } catch (err: any) {
      console.error('Error creating assignment:', err);
      console.error('Error details:', {
        message: err.message,
        stack: err.stack,
        response: err.response
      });
      const errorMessage = err.message || err.response?.data?.detail || 'Gagal membuat assignment. Silakan coba lagi.';
      setError(errorMessage);
    }
  };

  // Format date helper
  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleString('id-ID', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  // Helper untuk check apakah variable harus diisi oleh user ini
  const isVariableForCurrentUser = (variable: any): boolean => {
    if (!variable.group_id) return false; // Skip variables tanpa group
    if (!userGroups.length) return false;
    return userGroups.includes(variable.group_id);
  };

  // Helper function to convert month to Roman numeral
  const getMonthRoman = (month: number): string => {
    const romanNumerals: { [key: number]: string } = {
      1: 'I', 2: 'II', 3: 'III', 4: 'IV', 5: 'V', 6: 'VI',
      7: 'VII', 8: 'VIII', 9: 'IX', 10: 'X', 11: 'XI', 12: 'XII'
    };
    return romanNumerals[month] || 'I';
  };

  // Function to generate agreement number
  const generateAgreementNumber = (sequenceNumber: number = 1): string => {
    const now = new Date();
    const month = now.getMonth() + 1; // JavaScript months are 0-indexed
    const year = now.getFullYear();
    const monthRoman = getMonthRoman(month);
    
    // Format: 001/PKS-PSI/I/2025
    const sequence = String(sequenceNumber).padStart(3, '0');
    const prefix = 'PKS-PSI'; // Default prefix
    
    return `${sequence}/${prefix}/${monthRoman}/${year}`;
  };

  // Handle generate agreement number
  const handleGenerateAgreementNumber = () => {
    // For now, use 1 as default sequence. In production, this should come from API
    const generatedNumber = generateAgreementNumber(1);
    
    // Update formData
    setFormData(prev => ({
      ...prev,
      agreementNo: generatedNumber
    }));
    
    // Update no_agreement variable if exists
    const updatedVariables = variables.map((v: any) => {
      if (v.name === 'no_agreement' || v.name === '{no_agreement}') {
        return {
          ...v,
          value: generatedNumber
        };
      }
      return v;
    });
    
    // Check if no_agreement variable exists, if not add it
    const hasNoAgreementVar = updatedVariables.some((v: any) => 
      v.name === 'no_agreement' || v.name === '{no_agreement}'
    );
    
    if (!hasNoAgreementVar) {
      // Add no_agreement variable if it doesn't exist
      updatedVariables.push({
        name: 'no_agreement',
        value: generatedNumber,
        position: 0,
        group_id: null
      });
    }
    
    setVariables(updatedVariables);
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleVariableChange = (variableIndex: number, value: string) => {
    const updatedVariables = [...variables];
    updatedVariables[variableIndex] = {
      ...updatedVariables[variableIndex],
      value: value
    };
    setVariables(updatedVariables);
  };


  // Handle resize left panel
  useEffect(() => {
    const handleMouseMove = (e: MouseEvent) => {
      if (!isResizing) return;
      
      const newWidth = e.clientX;
      // Min width 300px, max width 800px
      if (newWidth >= 300 && newWidth <= 800) {
        setLeftPanelWidth(newWidth);
      }
    };

    const handleMouseUp = () => {
      setIsResizing(false);
    };

    if (isResizing) {
      document.addEventListener('mousemove', handleMouseMove);
      document.addEventListener('mouseup', handleMouseUp);
      document.body.style.cursor = 'col-resize';
      document.body.style.userSelect = 'none';
    }

    return () => {
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);
      document.body.style.cursor = '';
      document.body.style.userSelect = '';
    };
  }, [isResizing]);

  if (loading) {
    return (
      <div className={`min-h-screen flex items-center justify-center transition-colors duration-300 ${darkMode ? 'bg-gray-900' : 'bg-gray-50'}`}>
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-violet-600 mx-auto mb-4"></div>
          <p className={darkMode ? 'text-gray-300' : 'text-gray-600'}>Memuat data...</p>
        </div>
      </div>
    );
  }

  return (
    <div className={`h-screen flex flex-col overflow-hidden transition-colors duration-300 ${darkMode ? 'bg-gray-900' : 'bg-gray-50'}`}>
      {/* Header */}
      <div className={`border-b px-6 py-4 flex-shrink-0 transition-colors duration-300 ${darkMode ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-200'}`}>
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-8">
            <button
              onClick={() => navigate(`/agreement/${id}`)}
              className="text-violet-600 hover:text-violet-700 flex items-center text-sm font-medium"
            >
              <i className="fa-solid fa-arrow-left mr-2"></i>
              Back to Agreement Detail
            </button>
            <div>
              <h1 className={`text-xl font-semibold ${darkMode ? 'text-white' : 'text-gray-900'}`}>Drafting: {task?.title}</h1>
              <p className={`text-xs mt-0.5 ${darkMode ? 'text-gray-300' : 'text-gray-600'}`}>Edit variabel dan draft dokumen</p>
            </div>
          </div>
          <div className="flex items-center space-x-2">
            <button
              type="button"
              onClick={() => navigate(`/agreement/${id}`)}
              className={`px-4 py-2 text-sm font-medium rounded-lg transition-colors ${
                darkMode
                  ? 'text-gray-300 bg-gray-700 border border-gray-600 hover:bg-gray-600'
                  : 'text-gray-700 bg-white border border-gray-300 hover:bg-gray-50'
              }`}
            >
              Cancel
            </button>
            <button
              type="submit"
              form="drafting-form"
              disabled={saving}
              className="px-4 py-2 text-sm font-medium text-white bg-violet-600 rounded-lg hover:bg-violet-700 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {saving ? (
                <>
                  <i className="fa-solid fa-spinner fa-spin mr-2"></i>
                  Menyimpan...
                </>
              ) : (
                <>
                  <i className="fa-solid fa-save mr-2"></i>
                  Simpan Draft
                </>
              )}
            </button>
          </div>
        </div>
      </div>

      {/* Error Message */}
      {error && (
        <div className={`px-6 py-3 border-b text-sm flex-shrink-0 transition-colors duration-300 ${
          darkMode ? 'bg-red-900/30 border-red-800 text-red-300' : 'bg-red-50 border-red-200 text-red-700'
        }`}>
          {error}
        </div>
      )}

      {/* Main Content - Split View */}
      <div className="flex-1 flex overflow-hidden" style={{ height: 'calc(100vh - 120px)' }}>
        {/* Left Panel - Form & Variables */}
        <div 
          className={`border-r overflow-y-auto flex flex-col relative transition-colors duration-300 ${
            darkMode ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-200'
          }`}
          style={{ width: `${leftPanelWidth}px`, minWidth: '300px', maxWidth: '800px' }}
        >
          {/* Resize Handle */}
          <div
            className={`absolute right-0 top-0 bottom-0 w-1 hover:bg-violet-500 cursor-col-resize z-10 transition-colors ${
              darkMode ? 'bg-gray-600' : 'bg-gray-300'
            }`}
            onMouseDown={(e) => {
              e.preventDefault();
              setIsResizing(true);
            }}
          />
          
          <div className="p-6 space-y-6">
            {error && (
              <div className={`mb-6 px-4 py-3 border rounded-lg text-sm transition-colors duration-300 ${
                darkMode ? 'bg-red-900/30 border-red-800 text-red-300' : 'bg-red-50 border-red-200 text-red-700'
              }`}>
                {error}
              </div>
            )}

            <form id="drafting-form" className="space-y-8" onSubmit={handleSubmit}>
              {/* Basic Information */}
              <div className={`rounded-lg shadow-sm border p-6 transition-colors duration-300 ${
                darkMode ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-200'
              }`}>
                <h2 className={`text-lg font-semibold mb-6 ${darkMode ? 'text-white' : 'text-gray-900'}`}>Basic Information</h2>
                
                <div className="space-y-6">
                  <div>
                    <label className={`block text-sm font-medium mb-2 ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                      Title <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="text"
                      name="title"
                      value={formData.title}
                      onChange={handleInputChange}
                      placeholder="Masukkan title agreement"
                      className={`w-full px-4 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-violet-500 focus:border-transparent ${
                        darkMode
                          ? 'bg-gray-900 border-gray-700 text-white placeholder-gray-500'
                          : 'border-gray-300'
                      }`}
                      required
                    />
                  </div>

                  <div>
                    <label className={`block text-sm font-medium mb-2 ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                      Description
                    </label>
                    <textarea
                      name="description"
                      value={formData.description}
                      onChange={handleInputChange}
                      placeholder="Masukkan description agreement (opsional)"
                      rows={4}
                      className={`w-full px-4 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-violet-500 focus:border-transparent resize-none ${
                        darkMode
                          ? 'bg-gray-900 border-gray-700 text-white placeholder-gray-500'
                          : 'border-gray-300'
                      }`}
                    />
                  </div>

                  <div className="grid grid-cols-2 gap-6">
                    <div>
                      <label className={`block text-sm font-medium mb-2 ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                        Category <span className="text-red-500">*</span>
                      </label>
                      <select
                        key={`category-select-${categories.length}-${formData.categoryId}`}
                        name="categoryId"
                        value={formData.categoryId ? String(formData.categoryId) : ''}
                        onChange={handleInputChange}
                        className={`w-full px-4 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-violet-500 focus:border-transparent ${
                          darkMode
                            ? 'bg-gray-900 border-gray-700 text-white'
                            : 'bg-gray-50 border-gray-300'
                        }`}
                        required
                        disabled
                      >
                        <option value="">-- Pilih Category --</option>
                        {categories.map((cat) => (
                          <option key={cat.id} value={String(cat.id)}>
                            {cat.name}
                          </option>
                        ))}
                      </select>
                    </div>

                    <div>
                      <label className={`block text-sm font-medium mb-2 ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                        Agreement Type <span className="text-red-500">*</span>
                      </label>
                      <select
                        key={`agreement-type-select-${agreementTypes.length}-${formData.agreementTypeId}`}
                        name="agreementTypeId"
                        value={formData.agreementTypeId ? String(formData.agreementTypeId) : ''}
                        onChange={handleInputChange}
                        className={`w-full px-4 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-violet-500 focus:border-transparent ${
                          darkMode
                            ? 'bg-gray-900 border-gray-700 text-white'
                            : 'bg-gray-50 border-gray-300'
                        }`}
                        required
                        disabled
                      >
                        <option value="">-- Pilih Agreement Type --</option>
                        {agreementTypes.map((type) => (
                          <option key={type.id} value={String(type.id)}>
                            {type.name}
                          </option>
                        ))}
                      </select>
                    </div>
                  </div>

                  <div>
                    <label className={`block text-sm font-medium mb-2 ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                      Perusahaan <span className="text-red-500">*</span>
                    </label>
                    <select
                      name="companyId"
                      value={formData.companyId}
                      onChange={handleInputChange}
                      className={`w-full px-4 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-violet-500 focus:border-transparent ${
                        darkMode
                          ? 'bg-gray-900 border-gray-700 text-white'
                          : 'border-gray-300'
                      }`}
                      required
                    >
                      <option value="">-- Pilih Perusahaan --</option>
                      {companies.map((company) => (
                        <option key={company.id} value={company.id}>
                          {company.name}
                        </option>
                      ))}
                    </select>
                  </div>
                </div>
              </div>

              {/* No Agreement Section */}
              <div className={`rounded-lg shadow-sm border p-6 transition-colors duration-300 ${
                darkMode ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-200'
              }`}>
                <h2 className={`text-lg font-semibold mb-4 ${darkMode ? 'text-white' : 'text-gray-900'}`}>No Agreement</h2>
                <div>
                  <label className={`block text-sm font-medium mb-2 ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                    Nomor Agreement
                  </label>
                  <div className="flex items-center space-x-2">
                    <input
                      type="text"
                      name="agreementNo"
                      value={formData.agreementNo}
                      onChange={handleInputChange}
                      placeholder="001/PKS-PSI/I/2025"
                      className={`flex-1 px-4 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-violet-500 focus:border-transparent ${
                        darkMode
                          ? 'bg-gray-900 border-gray-700 text-white placeholder-gray-500'
                          : 'border-gray-300'
                      }`}
                    />
                    <button
                      type="button"
                      onClick={handleGenerateAgreementNumber}
                      className={`px-3 py-2 border rounded-lg text-xs font-medium transition-colors ${
                        darkMode
                          ? 'bg-violet-600 text-white border-violet-600 hover:bg-violet-700'
                          : 'bg-violet-600 text-white border-violet-600 hover:bg-violet-700'
                      }`}
                    >
                      <i className="fa-solid fa-wand-magic-sparkles mr-1"></i>
                      Generate
                    </button>
                  </div>
                  <p className={`text-xs mt-1 ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                    Format: 001/PKS-PSI/I/2025 (001 = auto increment, PKS-PSI = default, I = bulan romawi, 2025 = tahun)
                  </p>
                </div>
              </div>

              {/* Variables Section */}
              {variables.length > 0 && (
                <div className={`rounded-lg shadow-sm border p-6 transition-colors duration-300 ${
                  darkMode ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-200'
                }`}>
                  <div className="flex items-center justify-between mb-2">
                    <h2 className={`text-lg font-semibold ${darkMode ? 'text-white' : 'text-gray-900'}`}>Variables</h2>
                    <button
                      type="button"
                      onClick={() => setVariablesExpanded(!variablesExpanded)}
                      className="text-sm text-violet-600 hover:text-violet-700"
                    >
                      {variablesExpanded ? (
                        <>
                          <i className="fa-solid fa-chevron-up mr-1"></i>Sembunyikan
                        </>
                      ) : (
                        <>
                          <i className="fa-solid fa-chevron-down mr-1"></i>Tampilkan
                        </>
                      )}
                    </button>
                  </div>
                  {variablesExpanded && (
                    <>
                      <p className={`text-sm mb-6 ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                        Semua variables dari template. Variables yang harus Anda isi ditandai dengan badge "Required".
                      </p>
                      
                      <div className="space-y-4">
                    {variables
                      .sort((a: any, b: any) => (a.position || 0) - (b.position || 0))
                      .map((variable: any, index: number) => {
                        const variableValue = variable.value || '';
                        const isForCurrentUser = isVariableForCurrentUser(variable);
                        
                        return (
                          <div key={`${variable.name}-${variable.position}`}>
                            <div className="flex items-center justify-between mb-2">
                              <label className={`block text-sm font-medium ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                                {variable.name}
                                {isForCurrentUser && (
                                  <span className="ml-2 text-red-500">*</span>
                                )}
                              </label>
                              {isForCurrentUser && (
                                <span className={`px-2 py-1 text-xs font-medium rounded ${
                                  darkMode ? 'bg-red-900/50 text-red-300' : 'bg-red-100 text-red-700'
                                }`}>
                                  Required
                                </span>
                              )}
                            </div>
                            <textarea
                              value={variableValue}
                              onChange={(e) => {
                                const updatedVariables = [...variables];
                                updatedVariables[index] = {
                                  ...variable,
                                  value: e.target.value
                                };
                                setVariables(updatedVariables);
                              }}
                              placeholder={`Masukkan nilai untuk {${variable.name}}`}
                              rows={3}
                              className={`w-full px-4 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-violet-500 focus:border-transparent resize-y ${
                                isForCurrentUser 
                                  ? darkMode
                                    ? 'border-gray-600 bg-gray-900 text-white placeholder-gray-500'
                                    : 'border-gray-300'
                                  : darkMode
                                    ? 'border-gray-700 bg-gray-900/50 text-gray-400'
                                    : 'border-gray-200 bg-gray-50'
                              }`}
                              required={isForCurrentUser}
                              disabled={!isForCurrentUser}
                            />
                            {!isForCurrentUser && variable.group_id && (
                              <p className={`text-xs mt-1 ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                                Akan diisi oleh group lain
                              </p>
                            )}
                          </div>
                        );
                      })}
                  </div>

                  {/* Render Document Button */}
                  <div className={`mt-6 pt-6 border-t ${darkMode ? 'border-gray-700' : 'border-gray-200'}`}>
                    <button
                      type="button"
                      onClick={handleRenderDocument}
                      disabled={!documentHtml || !fileArrayBuffer}
                      className="w-full px-4 py-2 bg-violet-600 text-white text-sm font-medium rounded-lg hover:bg-violet-700 disabled:bg-gray-300 disabled:cursor-not-allowed flex items-center justify-center"
                    >
                      <i className="fa-solid fa-refresh mr-2"></i>
                      Update Document Preview
                    </button>
                    <p className={`text-xs mt-2 text-center ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                      Klik tombol di atas untuk memperbarui preview dokumen dengan nilai variable yang sudah diisi
                    </p>
                  </div>
                    </>
                  )}
                </div>
              )}

              {/* Assignment Section */}
              <div className={`rounded-lg shadow-sm border p-6 transition-colors duration-300 ${
                darkMode ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-200'
              }`}>
                <h2 className={`text-lg font-semibold mb-6 ${darkMode ? 'text-white' : 'text-gray-900'}`}>Assignment</h2>
                
                {/* Assignment Form */}
                <div className="space-y-4 mb-6">
                  <div>
                    <label className={`block text-sm font-medium mb-2 ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                      Assign to User
                    </label>
                    <select
                      name="user_id"
                      value={assignmentForm.user_id}
                      onChange={(e) => setAssignmentForm(prev => ({ ...prev, user_id: e.target.value }))}
                      className={`w-full px-4 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-violet-500 focus:border-transparent ${
                        darkMode
                          ? 'bg-gray-900 border-gray-700 text-white'
                          : 'bg-gray-50 border-gray-300'
                      }`}
                    >
                      <option value="">-- Pilih User --</option>
                      {users.map((u) => (
                        <option key={u.id} value={u.id}>
                          {u.name} ({u.email})
                        </option>
                      ))}
                    </select>
                  </div>

                  <div>
                    <label className={`block text-sm font-medium mb-2 ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                      Comment (Optional)
                    </label>
                    <textarea
                      name="comment"
                      value={assignmentForm.comment}
                      onChange={(e) => setAssignmentForm(prev => ({ ...prev, comment: e.target.value }))}
                      placeholder="Masukkan comment untuk assignment"
                      rows={3}
                      className={`w-full px-4 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-violet-500 focus:border-transparent resize-none ${
                        darkMode
                          ? 'bg-gray-900 border-gray-700 text-white placeholder-gray-500'
                          : 'border-gray-300'
                      }`}
                    />
                  </div>

                  <button
                    type="button"
                    onClick={handleAssignmentSubmit}
                    className="w-full px-4 py-2 text-sm font-medium text-white bg-violet-600 rounded-lg hover:bg-violet-700"
                  >
                    <i className="fa-solid fa-user-plus mr-2"></i>
                    Assign Task
                  </button>
                </div>

                {/* Assignment History */}
                <div className={`border-t pt-6 ${darkMode ? 'border-gray-700' : 'border-gray-200'}`}>
                  <div className="flex items-center mb-4">
                    <i className={`fa-solid fa-clock-rotate-left mr-2 ${darkMode ? 'text-violet-400' : 'text-violet-600'}`}></i>
                    <h3 className={`text-md font-semibold ${darkMode ? 'text-white' : 'text-gray-900'}`}>
                      Assignment History
                    </h3>
                  </div>
                  
                  {loadingAssignments ? (
                    <div className={`text-center py-8 ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                      <i className="fa-solid fa-spinner fa-spin text-2xl mb-2"></i>
                      <p className="text-sm">Memuat assignment history...</p>
                    </div>
                  ) : assignments.length === 0 ? (
                    <div className={`text-center py-8 ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                      <i className={`fa-solid fa-inbox text-3xl mb-2 ${darkMode ? 'text-gray-600' : 'text-gray-300'}`}></i>
                      <p className="text-sm">Belum ada assignment</p>
                    </div>
                  ) : (
                    <div className="space-y-3">
                      {assignments.map((assignment, index) => (
                        <div
                          key={assignment.id}
                          className={`group relative p-4 rounded-xl border transition-all duration-300 ${
                            darkMode 
                              ? 'bg-gradient-to-br from-gray-800/50 to-gray-900/50 border-gray-700 hover:border-violet-600/50 hover:shadow-lg hover:shadow-violet-900/20' 
                              : 'bg-white border-gray-200 hover:border-violet-300 hover:shadow-md hover:shadow-violet-100'
                          }`}
                        >
                          {/* Timeline indicator */}
                          {index < assignments.length - 1 && (
                            <div className={`absolute left-6 top-12 bottom-0 w-0.5 ${darkMode ? 'bg-gray-700' : 'bg-gray-200'}`}></div>
                          )}
                          
                          <div className="flex items-start space-x-3">
                            {/* Avatar/Icon */}
                            <div className={`flex-shrink-0 w-10 h-10 rounded-full flex items-center justify-center ${
                              darkMode ? 'bg-violet-900/50 text-violet-300' : 'bg-violet-100 text-violet-600'
                            }`}>
                              <i className="fa-solid fa-user text-sm"></i>
                            </div>
                            
                            {/* Content */}
                            <div className="flex-1 min-w-0">
                              {/* User Name and Email */}
                              <div className="mb-1">
                                <div className={`font-semibold text-sm ${darkMode ? 'text-white' : 'text-gray-900'}`}>
                                  {assignment.user?.name || 'Unknown User'}
                                </div>
                                <div className={`text-xs mt-0.5 ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                                  {assignment.user?.email || ''}
                                </div>
                              </div>
                              
                              {/* Timestamp */}
                              <div className={`mb-2 flex items-center space-x-1 text-xs ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                                <i className="fa-solid fa-clock"></i>
                                <span>{formatDate(assignment.created_at)}</span>
                              </div>
                              
                              {/* Comment */}
                              {assignment.comment && (
                                <div className={`mb-2 p-3 rounded-lg ${
                                  darkMode 
                                    ? 'bg-gray-800/50 border border-gray-700' 
                                    : 'bg-violet-50 border border-violet-100'
                                }`}>
                                  <div className="flex items-start space-x-2">
                                    <i className={`fa-solid fa-comment-dots mt-0.5 flex-shrink-0 ${
                                      darkMode ? 'text-violet-400' : 'text-violet-600'
                                    }`}></i>
                                    <p className={`text-sm leading-relaxed ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                                      {assignment.comment}
                                    </p>
                                  </div>
                                </div>
                              )}
                              
                              {/* Assigned By */}
                              <div className={`flex items-center space-x-1 text-xs ${darkMode ? 'text-gray-500' : 'text-gray-400'}`}>
                                <i className="fa-solid fa-user-tie"></i>
                                <span>Assigned by <span className={`font-medium ${darkMode ? 'text-gray-400' : 'text-gray-600'}`}>{assignment.creator?.name || 'Unknown'}</span></span>
                              </div>
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </div>

              {/* Submit Button */}
              <div className={`rounded-lg shadow-sm border p-6 transition-colors duration-300 ${
                darkMode ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-200'
              }`}>
                <div className="flex items-center justify-end space-x-4">
                  <button
                    type="button"
                    onClick={() => navigate(`/agreement/${id}`)}
                    className={`px-6 py-2 border rounded-lg transition-colors focus:outline-none focus:ring-2 focus:ring-gray-500 ${
                      darkMode
                        ? 'border-gray-600 text-gray-300 hover:bg-gray-700'
                        : 'border-gray-300 text-gray-700 hover:bg-gray-50'
                    }`}
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    disabled={saving}
                    className="px-6 py-2 bg-violet-600 text-white rounded-lg hover:bg-violet-700 disabled:bg-gray-300 disabled:cursor-not-allowed focus:outline-none focus:ring-2 focus:ring-violet-500 flex items-center"
                  >
                    {saving ? (
                      <>
                        <i className="fa-solid fa-spinner fa-spin mr-2"></i>
                        Menyimpan...
                      </>
                    ) : (
                      <>
                        <i className="fa-solid fa-save mr-2"></i>
                        Simpan Draft
                      </>
                    )}
                  </button>
                </div>
                {error && (
                  <div className={`mt-4 p-3 border rounded-lg transition-colors duration-300 ${
                    darkMode ? 'bg-red-900/30 border-red-800' : 'bg-red-50 border-red-200'
                  }`}>
                    <p className={`text-sm ${darkMode ? 'text-red-300' : 'text-red-600'}`}>{error}</p>
                  </div>
                )}
              </div>
            </form>
          </div>
        </div>

        {/* Right Panel - Document Preview */}
        <div className={`flex-1 overflow-hidden flex flex-col transition-colors duration-300 ${
          darkMode ? 'bg-gray-900' : 'bg-gray-100'
        }`}>
          <div className={`px-6 py-4 border-b flex-shrink-0 transition-colors duration-300 ${
            darkMode ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-200'
          }`}>
            <h2 className={`text-sm font-semibold ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>Document Preview</h2>
            {template && (
              <p className={`text-xs mt-1 ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>File: {template.file_name}</p>
            )}
          </div>
          
          <div className="flex-1 overflow-hidden">
            {/* Hidden container for renderAsync to avoid React conflicts */}
            <div 
              ref={hiddenRenderContainerRef}
              style={{ position: 'absolute', left: '-9999px', visibility: 'hidden' }}
            />
            
            <div className={`h-full overflow-y-auto overflow-x-hidden transition-colors duration-300 ${
              darkMode ? 'bg-gray-900' : 'bg-white'
            }`} style={{ paddingLeft: '40px', paddingRight: '40px' }}>
              {!fileArrayBuffer && (
                <div className="flex items-center justify-center h-full">
                  <div className="text-center">
                    <i className={`fa-solid fa-file-word text-4xl mb-4 ${darkMode ? 'text-gray-600' : 'text-gray-400'}`}></i>
                    <p className={darkMode ? 'text-gray-300' : 'text-gray-600'}>Dokumen akan muncul setelah template dimuat</p>
                  </div>
                </div>
              )}
              {fileArrayBuffer && (editorContent || documentHtml) && (
                <div 
                  key={`preview-${JSON.stringify(variables.map(v => ({ name: v.name, value: v.value })))}`}
                  ref={previewContainerRef}
                  dangerouslySetInnerHTML={{ __html: editorContent || documentHtml }}
                  style={{ 
                    minHeight: 0,
                    width: '100%',
                    maxWidth: '100%'
                  }}
                />
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default TaskDrafting;
